import { Box } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { FONT_SIZE } from "../../../theme/tokens";
import type { PbjAuditEmployeeRow } from "../../../utils/models";
import { useEditPbjEmployeeOverrideId, usePbjAuditEmployees } from "../hooks/usePbjAudit";
import { dataTableSx } from "../PredictionTab/colors";
import { boldNameColumn } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";
import { displayedPbjId, EmployeeIdOverrideCell } from "./EmployeeIdOverrideCell";
import { ManageOverridesDialog } from "./ManageOverridesDialog";

type Props = {
	startDate: string;
	endDate: string;
};

const PAY_TYPE_LABEL_KEY: Record<PbjAuditEmployeeRow["payTypeCode"], string> = {
	"1": "pbj_model.audit.employees.pay_type.exempt",
	"2": "pbj_model.audit.employees.pay_type.non_exempt",
	"3": "pbj_model.audit.employees.pay_type.contract",
};

/** Unique grid id — fold in the array index so equal (pbjId, name) rows never collide. */
type EmployeeGridRow = PbjAuditEmployeeRow & { __rowId: string };

const NAVIGABLE_ROW_CLASS = "pbj-employees-navigable-row";

// Layer a pointer cursor onto rows that deep-link to a nurse profile, on top of the shared PBJ table sx.
const dataTableSxForEmployees = (theme: Theme) => ({
	...dataTableSx(theme),
	[`& .MuiDataGrid-row.${NAVIGABLE_ROW_CLASS}`]: { cursor: "pointer" },
});

/**
 * Employees submission sheet — the PBJ employee roster for the window. Migrated to the canonical
 * SystemDataGrid (opaque pinned header, skeleton loader, internal scroll). Double-clicking a row that
 * links to a nurse profile deep-links there, mirroring the previous table's behavior.
 */
export const EmployeesTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { chosenDepartment } = useChosenDepartment();
	const { data, isLoading, isError } = usePbjAuditEmployees(startDate, endDate);
	const overrideIdMutation = useEditPbjEmployeeOverrideId();
	const [manageOverridesOpen, setManageOverridesOpen] = useState(false);

	const rows = useMemo<EmployeeGridRow[]>(() => (data ?? []).map((r, i) => ({ ...r, __rowId: `${i}|${r.pbjId}|${r.name}` })), [data]);

	// Mirrors the nurse deep-link shape used across the app (e.g. EmployeeChangelog, PtoGapViewSection).
	const goToProfile = useCallback(
		(nurseId: number | null) => {
			if (nurseId == null) return; // load_only employees have no linked nurse profile to open
			navigate(`/${chosenDepartment.department_id}/nurse/${nurseId}`);
		},
		[navigate, chosenDepartment.department_id],
	);

	const columns: GridColDef<EmployeeGridRow>[] = useMemo(
		() => [
			{
				field: "pbjId",
				headerName: t("pbj_model.audit.employees.cols.id"),
				minWidth: 150,
				flex: 1,
				// Sort on the EFFECTIVE id (override ?? synced) so the order matches what the cell shows.
				valueGetter: (_value, row) => displayedPbjId(row),
				renderCell: (params: GridRenderCellParams<EmployeeGridRow>) => (
					<Box sx={{ fontFamily: "monospace", fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", height: "100%", display: "flex", alignItems: "center" }}>
						<EmployeeIdOverrideCell
							row={params.row}
							isSaving={overrideIdMutation.isPending}
							onSave={(pbjOverrideEmployeeId) => overrideIdMutation.mutateAsync({ pbjEmployeeId: params.row.pbjId, pbjOverrideEmployeeId })}
						/>
					</Box>
				),
			},
			boldNameColumn<EmployeeGridRow>("name", t("pbj_model.audit.employees.cols.name")),
			{
				field: "hireDate",
				headerName: t("pbj_model.audit.employees.cols.hire"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			{
				field: "terminationDate",
				headerName: t("pbj_model.audit.employees.cols.term"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			{
				field: "payTypeCode",
				headerName: t("pbj_model.audit.employees.cols.pay_type"),
				minWidth: 160,
				flex: 1,
				valueGetter: (_value, row) => `${row.payTypeCode} — ${t(PAY_TYPE_LABEL_KEY[row.payTypeCode])}`,
			},
		],
		[t, overrideIdMutation.isPending, overrideIdMutation.mutateAsync],
	);

	return (
		<>
			<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
				<SystemDataGrid<EmployeeGridRow>
					rows={rows}
					columns={columns}
					getRowId={(r) => r.__rowId}
					// Match on the EFFECTIVE id so search stays WYSIWYG once an override is set.
					searchableColumns={[{ field: "pbjId", getValue: displayedPbjId }, "name"]}
					searchPlaceholder={t("pbj_model.audit.employees.search")}
					loading={isLoading}
					error={isError}
					errorMessage={t("pbj_model.audit.employees.failed")}
					emptyState={<EmptyState title={t("pbj_model.audit.employees.empty")} dataTestId="pbj-model-audit-employees-empty" />}
					defaultPageSize={25}
					sx={(theme) => dataTableSxForEmployees(theme)}
					dataGridProps={{
						onRowDoubleClick: (params) => goToProfile((params.row as EmployeeGridRow).nurseId),
						getRowClassName: (params) => ((params.row as EmployeeGridRow).nurseId != null ? NAVIGABLE_ROW_CLASS : ""),
					}}
					trailingToolbarNodes={
						<SystemButton
							buttonVariant="secondary"
							size="small"
							noFullWidthMobile
							sx={{ ml: "auto" }}
							onClick={() => setManageOverridesOpen(true)}
							data-testid="pbj-employees-manage-overrides"
						>
							{t("pbj_model.override_manage.button")}
						</SystemButton>
					}
					dataTestId="pbj-model-audit-employees"
				/>
			</Box>
			<ManageOverridesDialog
				open={manageOverridesOpen}
				onClose={() => setManageOverridesOpen(false)}
				departmentId={chosenDepartment.department_id}
				rows={data ?? []}
			/>
		</>
	);
};
